import { z } from "zod";
import { actorIdentity, actorIdentitySchema, requireCapability, requireIndependentContractAcceptance, requireTenant, } from "./authority.js";
import { assertCurrentAcceptedContractEnvelope, contractBinding, } from "./contract.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, attemptBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, } from "./identifiers.js";
export const workAttemptSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    binding: attemptBindingSchema,
    worker: actorIdentitySchema,
    adapter: z
        .object({
        mode: z.enum(["fully_mediated", "cooperative", "passive"]),
        version: z.string().trim().min(1),
        capabilities: z.array(protocolIdSchema),
        negotiationId: protocolIdSchema.nullable(),
    })
        .strict(),
    state: z.enum(["OPEN", "SUBMITTED", "SUPERSEDED", "ABANDONED"]),
    openedAt: protocolTimestampSchema,
    submittedAt: protocolTimestampSchema.nullable(),
    outputSnapshotDigest: sha256DigestSchema.nullable(),
    supersedesAttemptId: protocolIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.worker.tenantId !== value.binding.tenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["worker", "tenantId"],
            message: "worker tenant must match attempt binding",
        });
    }
    if (value.state === "SUBMITTED" && (!value.submittedAt || !value.outputSnapshotDigest)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["state"],
            message: "submitted attempts require submittedAt and outputSnapshotDigest",
        });
    }
});
const ATTEMPT_TRANSITIONS = {
    OPEN: ["SUBMITTED", "SUPERSEDED", "ABANDONED"],
    SUBMITTED: ["SUPERSEDED"],
    SUPERSEDED: [],
    ABANDONED: [],
};
export function canTransitionWorkAttempt(from, to) {
    return from === to || ATTEMPT_TRANSITIONS[from].includes(to);
}
export function createWorkAttempt(input) {
    const contract = assertCurrentAcceptedContractEnvelope(input.contract);
    requireTenant(input.worker, contract.content.tenantId);
    requireCapability(input.worker, "attempt:execute");
    requireIndependentContractAcceptance({
        accepter: {
            ...contract.acceptance.acceptedBy,
            credentialId: contract.acceptance.signature.keyId,
            authenticatedAt: contract.acceptance.acceptedAt,
            capabilities: ["contract:accept"],
        },
        workerActorId: input.worker.actorId,
        tenantId: contract.content.tenantId,
    });
    return workAttemptSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        binding: {
            ...contractBinding(contract),
            attemptId: input.id,
            inputSnapshotDigest: input.inputSnapshotDigest,
        },
        worker: actorIdentity(input.worker),
        adapter: input.adapter ?? {
            mode: "passive",
            version: "excellent-agent-adapter.v1",
            capabilities: ["stable_identity", "evidence_upload"],
            negotiationId: null,
        },
        state: "OPEN",
        openedAt: input.openedAt,
        submittedAt: null,
        outputSnapshotDigest: null,
        supersedesAttemptId: input.supersedesAttemptId ?? null,
    });
}
export function submitWorkAttempt(input) {
    const attempt = workAttemptSchema.parse(input.attempt);
    if (!canTransitionWorkAttempt(attempt.state, "SUBMITTED")) {
        throw new Error(`attempt ${attempt.binding.attemptId} is not open`);
    }
    return workAttemptSchema.parse({
        ...attempt,
        state: "SUBMITTED",
        outputSnapshotDigest: input.outputSnapshotDigest,
        submittedAt: input.submittedAt,
    });
}
export function supersedeWorkAttempt(input) {
    const attempt = workAttemptSchema.parse(input.attempt);
    if (!canTransitionWorkAttempt(attempt.state, "SUPERSEDED")) {
        throw new Error(`attempt ${attempt.binding.attemptId} cannot be superseded`);
    }
    return workAttemptSchema.parse({ ...attempt, state: "SUPERSEDED" });
}
